import { DateRangeField, MultiSelect } from "@tellescope/ui";
import type { PhoneLogType } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import type { PhoneLogsFilters } from "./phoneLogsFilters";

export interface PhoneLogsFilterConfigFormProps {
  stagedFilters: PhoneLogsFilters;
  onStagedFiltersChange: (filters: PhoneLogsFilters) => void;
  /** Distinct direction values present in the loaded logs. */
  directionOptions: string[];
  /** Distinct status values present in the loaded logs. */
  statusOptions: string[];
}

const isPhoneLogType = (value: string): value is PhoneLogType => value === "SMS" || value === "Voice";

export const PhoneLogsFilterConfigForm = ({
  stagedFilters,
  onStagedFiltersChange,
  directionOptions,
  statusOptions,
}: PhoneLogsFilterConfigFormProps) => {
  const { t } = useTranslation("settings");

  const typeOptions = [
    { label: t("SMS"), value: "SMS" },
    { label: t("Voice"), value: "Voice" },
  ];

  return (
    <div className="space-y-2 font-semibold">
      <h3>{t("Filters")}</h3>
      <div className="space-y-4 font-normal">
        <div className="space-y-1.5">
          <DateRangeField
            label={t("Date Range")}
            value={stagedFilters.dateRange ?? {}}
            onChange={(value) =>
              onStagedFiltersChange({
                ...stagedFilters,
                dateRange: value.from !== undefined || value.to !== undefined ? value : undefined,
              })
            }
            className="w-64"
          />
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Type")}</span>
          <MultiSelect
            options={typeOptions}
            value={stagedFilters.types ?? []}
            onValueChange={(values) => {
              const types = values.filter(isPhoneLogType);
              onStagedFiltersChange({ ...stagedFilters, types: types.length > 0 ? types : undefined });
            }}
            placeholder={t("Any type")}
            className="w-64"
          />
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Direction")}</span>
          <MultiSelect
            options={directionOptions.map((d) => ({ label: d, value: d }))}
            value={stagedFilters.directions ?? []}
            onValueChange={(directions) =>
              onStagedFiltersChange({ ...stagedFilters, directions: directions.length > 0 ? directions : undefined })
            }
            placeholder={t("Any direction")}
            className="w-64"
          />
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Status")}</span>
          <MultiSelect
            options={statusOptions.map((s) => ({ label: s, value: s }))}
            value={stagedFilters.statuses ?? []}
            onValueChange={(statuses) =>
              onStagedFiltersChange({ ...stagedFilters, statuses: statuses.length > 0 ? statuses : undefined })
            }
            placeholder={t("Any status")}
            className="w-64"
          />
        </div>
      </div>
    </div>
  );
};
